import { useCallback, useRef, useState } from 'react';
import { ViewportPortal, useStore } from '@xyflow/react';
import type { RunDayDivider } from '@mct/shared';
import { cn } from '../../lib/utils';

/**
 * Renders all phase day-divider lines as one viewport-portal overlay.
 *
 * Why not a custom ReactFlow node? The node-based approach was clipped
 * by the renderer's per-node bounding-box measurement, so the visible
 * stroke ended up being only as tall as the viewport — useless when
 * the task chart extends well beyond. ViewportPortal puts the lines in
 * canvas-coordinate space directly, lets us span an arbitrarily large
 * Y range (-50_000 → +50_000 px), and keeps drag math explicit.
 *
 * Drag: pointerdown on the line starts a manual drag loop. We record
 * the starting screen X and the starting flow X, then convert pointer
 * deltas via the current zoom factor on every pointermove. The parent
 * receives `onDragChange` for optimistic UI updates and `onDragEnd`
 * for the Firestore persist.
 */
export function DayDividerLines({
  dividers,
  canEdit,
  onDragChange,
  onDragEnd,
}: {
  dividers: readonly RunDayDivider[];
  canEdit: boolean;
  /** Called on every pointermove during drag with the new flow X. */
  onDragChange: (id: string, nextX: number) => void;
  /** Called on pointerup with the final flow X (persist target). */
  onDragEnd: (id: string, nextX: number) => void;
}) {
  // Per-scalar selectors so each subscription returns a primitive
  // (Object.is-stable). Subscribing to the whole transform tuple
  // would re-render on every store emit even when zoom is unchanged.
  const zoom = useStore((s) => s.transform[2]);
  const [dragging, setDragging] = useState<string | null>(null);
  const dragRef = useRef<{
    id: string;
    startScreenX: number;
    startFlowX: number;
  } | null>(null);

  const onPointerDown = useCallback(
    (e: React.PointerEvent, divider: RunDayDivider) => {
      if (!canEdit) return;
      // Don't let the canvas pan while we're dragging the line.
      e.stopPropagation();
      e.preventDefault();
      dragRef.current = {
        id: divider.id,
        startScreenX: e.clientX,
        startFlowX: divider.x,
      };
      setDragging(divider.id);
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    },
    [canEdit],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const deltaScreen = e.clientX - d.startScreenX;
      // Convert screen pixels to flow pixels via 1 / zoom.
      const nextFlowX = d.startFlowX + deltaScreen / Math.max(zoom, 0.0001);
      onDragChange(d.id, nextFlowX);
    },
    [onDragChange, zoom],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const deltaScreen = e.clientX - d.startScreenX;
      const finalX = d.startFlowX + deltaScreen / Math.max(zoom, 0.0001);
      dragRef.current = null;
      setDragging(null);
      onDragEnd(d.id, finalX);
    },
    [onDragEnd, zoom],
  );

  return (
    <ViewportPortal>
      {dividers.map((d) => (
        <div
          key={d.id}
          // Position in canvas / flow coordinates. ViewportPortal
          // applies the viewport transform automatically.
          style={{
            position: 'absolute',
            // Centre a 12 px hit-test column on the divider's x.
            left: d.x - 6,
            top: -50000,
            width: 12,
            height: 100000,
            // The pointer-events on the wrapper match canEdit so a
            // read-only phase doesn't intercept canvas pan attempts.
            pointerEvents: canEdit ? 'auto' : 'none',
            cursor: canEdit ? 'col-resize' : 'default',
            // zIndex: above edges (default 0) but below cards (1000).
            zIndex: 100,
          }}
          onPointerDown={(e) => onPointerDown(e, d)}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
        >
          <div
            // The visible 2 px stroke, centred inside the hit-test
            // column. Stroke colour bumps on hover and during drag so
            // the user gets immediate feedback.
            className={cn(
              'mx-auto h-full transition-colors',
              dragging === d.id
                ? 'bg-indigo-600'
                : 'bg-indigo-400/80 hover:bg-indigo-600',
            )}
            style={{ width: 2 }}
          />
        </div>
      ))}
    </ViewportPortal>
  );
}
